import { DOCUMENT, CommonModule, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { APP_LANGUAGES, DEFAULT_FONT_SIZE, DEFAULT_LANGUAGE } from '../../constants/global-header.constant';
import { LOCAL_STORAGE_KEYS } from '../../enums/local-storage.enum';
import { Theme } from '../../enums/theme.enum';
import { IAppLanguage } from '../../models/global-header.model';
import { StorageService } from '../../services/storage/storage-service';
import { ThemeService } from '../../services/theme/theme-service';

@Component({
    selector: 'pims-global-header',
    imports: [CommonModule, TranslatePipe, MatMenuModule, NgOptimizedImage],
    templateUrl: './global-header.component.html',
    styleUrl: './global-header.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GlobalHeaderComponent implements OnInit {
    private readonly translateService = inject(TranslateService);
    private readonly storageService = inject(StorageService);
    private readonly themeService = inject(ThemeService);
    private readonly document = inject(DOCUMENT);

    readonly activeButton = signal<'decrease' | 'reset' | 'increase'>('reset');
    readonly appLanguages: IAppLanguage[] = APP_LANGUAGES;
    readonly defaultFontSize = signal<number>(DEFAULT_FONT_SIZE);
    readonly selectedLanguage = signal<IAppLanguage>(DEFAULT_LANGUAGE);
    readonly theme = this.themeService.theme;

    ngOnInit(): void {
        this.loadSavedLanguage();
    }

    loadSavedLanguage(): void {
        const lang = this.storageService.getLocalItem<IAppLanguage>(LOCAL_STORAGE_KEYS.SELECTED_LANGUAGE);
        if (lang) {
            this.selectedLanguage.set(lang);
            this.translateService.use(lang.langCode);
        } else {
            this.translateService.use(this.selectedLanguage().langCode);
        }
    }

    setTheme(theme: string): void {
        void this.themeService.setTheme(theme as Theme);
    }

    onLanguageChange(lang: IAppLanguage): void {
        this.selectedLanguage.set(lang);
        this.translateService.use(lang.langCode);
        this.storageService.setLocalItem(LOCAL_STORAGE_KEYS.SELECTED_LANGUAGE, lang);
    }

    changeFontSize(action: 'decrease' | 'reset' | 'increase'): void {
        let newSize = this.defaultFontSize();

        if (action === 'reset') {
            newSize = 12;
        } else if (action === 'decrease' && this.defaultFontSize() > 10) {
            newSize = this.defaultFontSize() - 1;
        } else if (action === 'increase' && this.defaultFontSize() < 14) {
            newSize = this.defaultFontSize() + 1;
        }

        this.defaultFontSize.set(newSize);
        this.activeButton.set(action);

        if (this.document.documentElement) {
            this.document.documentElement.style.fontSize = `${newSize}px`;
        }
    }
}
